import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { ResumePreview } from "~/components/ResumePreview";
import { client } from "~/lib/orpc-client";
import type { ResumeData } from "@alchema/shared";

export const Route = createFileRoute("/shared/$shareToken")({
  component: SharedResumePage,
});

function SharedResumePage() {
  const { shareToken } = Route.useParams();
  const [data, setData] = React.useState<ResumeData | null>(null);
  const [title, setTitle] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [password, setPassword] = React.useState("");
  const [needsPassword, setNeedsPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const fetchResume = React.useCallback(
    async (pw?: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await client.resume.getShared({
          shareToken,
          password: pw || undefined,
        });
        setData(result.data as ResumeData);
        setTitle(result.title);
        setNeedsPassword(false);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Error";
        if (msg.includes("Invalid password") || msg.includes("password")) {
          setNeedsPassword(true);
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [shareToken],
  );

  React.useEffect(() => {
    fetchResume();
  }, [fetchResume]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div
          className="animate-spin h-8 w-8 rounded-full border-2 border-t-transparent"
          style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div
          className="rounded-[13px] p-8 w-full max-w-sm space-y-4"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border-subtle)",
            boxShadow: "0 2px 8px oklch(0 0 0 / 0.08), 0 8px 24px oklch(0 0 0 / 0.12)",
          }}
        >
          <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>
            Password Required
          </h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            This resume is password protected.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchResume(password);
            }}
            className="w-full rounded-[10px] px-3 py-2 text-sm"
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text)",
            }}
            placeholder="Enter password"
          />
          <button
            type="button"
            onClick={() => fetchResume(password)}
            className="w-full rounded-[10px] px-4 py-2 text-sm font-medium transition-colors"
            style={{
              background: "var(--accent)",
              color: "#fff",
            }}
          >
            View Resume
          </button>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div
          className="rounded-[13px] p-8 text-center"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <h3 className="text-base font-semibold" style={{ color: "var(--text)" }}>
            Resume Not Found
          </h3>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {error || "This link may have been revoked."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <div
        className="border-b px-5 py-3"
        style={{
          borderColor: "var(--border-subtle)",
          background: "var(--surface)",
        }}
      >
        <h1 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
          {title}
        </h1>
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          Shared resume — read only
        </p>
      </div>
      <div className="flex-1">
        <ResumePreview data={data} />
      </div>
    </div>
  );
}
